import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { basicTimerMsg } from '../../../actions/swal_msg';
import { getPostalcodeData } from '../../../actions/checkout';
import { updShippingAddress } from '../../../actions/user-account/address/ua_address';
import { userUpdShippingAddressFormValidator } from '../../../validators/UserShippingAddressValidator';

//CSS
import './address.css';

//MUI
import {
    Typography, TextField, InputLabel, MenuItem, Checkbox,
    FormControl, Select, InputAdornment, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Button,
    Snackbar, Box, Grid, FormGroup, FormControlLabel,
    Alert as MuiAlert
} from '@mui/material';
import SignpostIcon from '@mui/icons-material/Signpost';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';

// regex números
import { regex_num } from '../../../constants/regex';
import { USER_ADDRESS } from '../../../constants/user/user_constants';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddressEdition = ({
    hide, addresses, setAddresses, activeAddress, setActiveAddress
}) => {
    const {
        _id, name, lastname, tel, street, ext_num,
        int_num, crossing_streets, postalcode,
        colony, city, state, references, by_default
    } = activeAddress;

    // información recuperada con el código postal
    // colonias disponibles para campo de envío
    const [colonies, setColonies] = useState([]);

    // campo de código postal manipulado
    const [touched, setTouched] = useState(false);

    // deshabilitación de campos
    const [disabledFields, setDisabledFields] = useState({
        cityInput: false,
        stateInput: false
    });
    const { cityInput, stateInput } = disabledFields;

    useEffect(() => {
        if (touched && postalcode.length === 5)
            postalcodeData();
    }, [postalcode]);

    const postalcodeData = async () => {
        const resp = await getPostalcodeData(postalcode);
        if (resp.status) {
            const { resp: { municipio, estado, asentamiento } } = resp;
            setActiveAddress(e => ({
                ...e,
                colony: '',
                city: municipio,
                state: estado
            }));
            setColonies(asentamiento);
            // deshabilita campos
            setDisabledFields({
                ...disabledFields,
                cityInput: true, stateInput: true
            });
        } else {
            // limpia colonias disponibles
            setColonies([]);
            // habilita campos
            setDisabledFields({
                ...disabledFields,
                cityInput: false, stateInput: false
            });
        }
    };

    const handleChange = ({ target }) => {
        setActiveAddress(e => ({ ...e, by_default: target.checked }));
    };

    const handleInputChange = ({ target }) => {
        const name = target.name;
        const value = target.value;
        // propiedades con únicamente números
        if ((['ext_num', 'tel', 'postalcode'].includes(name))) {
            if (name === 'postalcode') { // campo manipulado
                if (!touched) setTouched(true);
            }
            if (value === '' || regex_num.test(value))
                setActiveAddress(e => ({ ...e, [name]: value }));
        } else
            setActiveAddress(e => ({ ...e, [name]: value }));
    };

    //Dialog
    const [open, setOpen] = useState(false);

    const handleClickOpenDialog = () => {
        if ( // asegurarse de que por lo menos una dirección esté como predeterminada
            addresses.filter(e => e._id !== _id).find(e => e.by_default)
            || by_default
        )
            setOpen(true);
        else {
            basicTimerMsg('Debe tener por lo menos una dirección como predeterminada');
            setActiveAddress(e => ({ ...e, by_default: true }));
        }
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    //Alert
    const [alert, setAlert] = useState(false)
    const handleCloseAlert = () => {
        setAlert(false);
    }

    //Funcion cuando acepta editar los cambios enel dialog
    const handleEditAddress = async () => {
        setOpen(false);
        if (userUpdShippingAddressFormValidator(activeAddress)) {
            const req = await updShippingAddress(activeAddress);
            if (req.status) {
                setAddresses(e => e.map(
                    u => (u._id === _id) ?
                        { ...u, ...activeAddress }
                    :
                        { ...u, by_default: by_default ? false : u.by_default }
                ));
                setActiveAddress(USER_ADDRESS);
                setAlert(true);
                hide();
            }
        }
    }

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ my: 'auto' }}>
                    <Typography variant='h5'>Editar dirección</Typography>
                </Box>

                <Box sx={{ my: 'auto' }}>
                    <Link to={""} onClick={hide} style={{ fontSize: '25px', color: 'red' }}>
                        <i className="fa-solid fa-xmark"></i>
                    </Link>
                </Box>
            </Box>
            {/* Form */}
            <form action="">
                {/* Row */}
                <Grid container spacing={2}>
                    {/* Nombres */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Nombre(s)" variant="standard"
                                name="name"
                                value={name}
                                inputProps={{ maxLength: 60 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-user"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Apellidos */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Apellidos" variant="standard"
                                name="lastname"
                                value={lastname}
                                inputProps={{ maxLength: 60 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-user-group"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Dirección */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Dirección" variant="standard"
                                name="street"
                                value={street}
                                inputProps={{ maxLength: 100 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-location-dot"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Número exterior */}
                    <Grid item xl={3} lg={3} md={6} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Número exterior" variant="standard"
                                name="ext_num"
                                value={ext_num}
                                inputProps={{ maxLength: 4 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-hashtag"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Número interior */}
                    <Grid item xl={3} lg={3} md={6} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Número interior" variant="standard"
                                name="int_num"
                                value={int_num}
                                inputProps={{ maxLength: 8 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-hashtag"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Cruce de calles */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Entre las calles" variant="standard"
                                name="crossing_streets"
                                value={crossing_streets}
                                inputProps={{ maxLength: 200 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-road"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Código postal */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Código postal" variant="standard"
                                name="postalcode"
                                value={postalcode}
                                inputProps={{ maxLength: 5 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MarkunreadMailboxIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Municipio */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Municipio" variant="standard"
                                name="city"
                                value={city}
                                disabled={cityInput}
                                inputProps={{ maxLength: 100 }}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                    </Grid>

                    {/* Colonia */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        {
                            colonies.length <= 0 ?
                                <FormControl fullWidth>
                                    <TextField id="" label="Colonia" variant="standard"
                                        name="colony"
                                        value={colony}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                                :
                                <FormControl variant="standard" fullWidth>
                                    <InputLabel>Colonia</InputLabel>
                                    <Select
                                        id=""
                                        name="colony"
                                        value={colony}
                                        onChange={handleInputChange}
                                        label="Colonia">
                                        {
                                            colonies.map(({ nombre }) =>
                                                <MenuItem key={nombre}
                                                    value={nombre}>{nombre}
                                                </MenuItem>
                                            )
                                        }
                                    </Select>
                                </FormControl>
                        }
                    </Grid>

                    {/* Estado */}
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Estado" variant="standard"
                                name="state"
                                value={state}
                                disabled={stateInput}
                                inputProps={{ maxLength: 100 }}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                    </Grid>

                    {/* Teléfono */}
                    <Grid item xl={6} ld={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Teléfono" variant="standard"
                                name="tel"
                                value={tel}
                                inputProps={{ maxLength: 10 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa-solid fa-phone"></i>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Referencias */}
                    <Grid item xl={6} ld={6} md={6} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <TextField id="" label="Referencias" variant="standard"
                                name="references"
                                value={references}
                                inputProps={{ maxLength: 200 }}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SignpostIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xl={6} ld={6} md={6} sm={12} xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={by_default}
                                        onChange={handleChange} />
                                }
                                label="Seleccionar como predeterminada"
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
                {/* Fin row */}
            </form>
            {/* Fin form */}
            {/* button editar dirección */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                <Button variant='contained'
                    onClick={handleClickOpenDialog}
                >
                    Guardar cambios</Button>
            </Box>
            {/* Fin button editar dirección */}

            {/* Dialog */}
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"¿Está seguro que desea guardar los cambios?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Su dirección de envío será actualizada. Puede utilizar sus direcciones de envío para agilizar sus compras en la tienda de Nefrópolis
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleEditAddress}>
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={alert} autoHideDuration={2500} onClose={handleCloseAlert}>
                <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
                    Se han guardado los cambios
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddressEdition;